/**
 * ============================================
 * USE ALERT MONITOR HOOK
 * ============================================
 * 
 * Monitors environment health and triggers alerts.
 * Works with AlertContext to manage alarm state.
 * 
 * Note: CCU alarms are handled separately in useCCU.ts with local storage.
 */

import { useEffect, useRef } from 'react';
import { useAlerts } from '@/context/AlertContext';
import type { EnvironmentConfig } from '@/config';
import type { ServiceHealth } from './useEnvironmentData';

interface UseAlertMonitorProps {
  environment: EnvironmentConfig;
  servicesHealth: ServiceHealth[];
}

export function useAlertMonitor({
  environment,
  servicesHealth,
}: UseAlertMonitorProps): void {
  const { addAlert, resolveAlert } = useAlerts();
  const prevHealthRef = useRef<Record<string, string>>({});

  useEffect(() => {
    // Check each service's health
    for (const service of servicesHealth) {
      const alertId = `${environment.id}-${service.serviceId}`;
      const prevStatus = prevHealthRef.current[service.serviceId];
      
      // Service became critical
      if (service.status === 'critical' && prevStatus !== 'critical') {
        addAlert({
          id: alertId,
          type: `${service.serviceId}_down`,
          severity: 'critical',
          title: `${service.serviceId.toUpperCase()} Down`,
          message: `${service.serviceId} is down in ${environment.name}: ${service.message}`,
          environmentId: environment.id,
          environmentName: environment.name,
          timestamp: new Date(),
          acknowledged: false,
        });
      }
      
      // Service recovered
      if (service.status === 'healthy' && prevStatus === 'critical') {
        resolveAlert(alertId);
      }
      
      prevHealthRef.current[service.serviceId] = service.status;
    }
  }, [environment, servicesHealth, addAlert, resolveAlert]);
}
